import {
  ConsultationStatus,
  Document,
  LiteratureReference,
} from '@/shared/api/transcriptor/transcriptorApi';
import { debug } from '@/shared/lib';

import { ConsultationPersonInfo } from '@/entities/transcriptor';

import { ConsultationSection } from '../config';

const PROCESSING_STATUSES: ConsultationStatus[] = [
  'created',
  'recording',
  'processing',
];

const GENERATING_DOCUMENT_STATUSES: Document['status'][] = [
  'pending',
  'generating',
];

/**
 * Checks if consultation is still being processed (not yet succeeded or failed)
 */
export const isConsultationProcessing = (
  status?: ConsultationStatus,
): boolean => {
  return Boolean(status && PROCESSING_STATUSES.includes(status));
};

/**
 * Checks if a document status means generation is still in progress.
 * The scribe API creates a stub for every ordered document up front with
 * `status=pending` and fills it in (`generating` → `completed`) as generation
 * progresses, so a pending/generating status is the source of truth for the
 * per-tab in-progress indicator.
 */
export const isGeneratingDocumentStatus = (
  status?: Document['status'],
): boolean => {
  return Boolean(status && GENERATING_DOCUMENT_STATUSES.includes(status));
};

/**
 * Checks if a document is still being generated by the backend.
 */
export const isDocumentGenerating = (doc?: Document | null): boolean => {
  return isGeneratingDocumentStatus(doc?.status);
};

/**
 * A failed document may still carry the sections of its last attempt: before
 * WA-6559 the backend persisted them alongside the failure, and those older
 * consultations must keep rendering what they have.
 */
export const isDocumentFailedWithoutContent = (
  doc?: Document | null,
): boolean => {
  if (doc?.status !== 'failed') {
    return false;
  }

  return !doc.sections?.some(
    (section) =>
      Boolean(section.user_content?.text.trim()) ||
      section.content?.some((element) => Boolean(element.text?.trim())),
  );
};

/**
 * Checks if a consultation section has empty content
 */
export const isEmptyField = (section: ConsultationSection): boolean => {
  return !section.content.length || !section.content[0].text.trim();
};

/**
 * Safely formats a date string to localized format
 */
export const formatDateSafely = (
  dateString: string | undefined,
  locale: string,
  timeZone?: string,
): string => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone,
    });
  } catch (error) {
    debug('Error formatting date:', error);
    return '';
  }
};

export const formatTimecode = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/** Builds the canonical DOI resolver URL for a literature reference. */
export const getDoiUrl = (doi: string): string => `https://doi.org/${doi}`;

/** Builds the canonical PubMed URL for a literature reference. */
export const getPubMedUrl = (pmid: string): string =>
  `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`;

/**
 * Resolves a single external URL for a literature reference, preferring the DOI
 * link and falling back to PubMed. Returns null when the reference carries
 * neither identifier. Shared by the references list and the inline citations so
 * the URL formats can't drift apart.
 */
export const getReferenceUrl = (
  reference: LiteratureReference,
): string | null => {
  if (reference.doi) {
    return getDoiUrl(reference.doi);
  }
  if (reference.pmid) {
    return getPubMedUrl(reference.pmid);
  }
  return null;
};

/**
 * Reports whether a citation token is a bare reference number (e.g. `11`).
 * Used to skip separators (`,`, `-`, whitespace) when linking citations, and to
 * avoid `Number('')`/`Number(' ')` collapsing to `0`.
 */
export const isCitationNumber = (token: string): boolean => /^\d+$/.test(token);

export const getPersonFullName = (
  info?: ConsultationPersonInfo | null,
): string | null => {
  if (!info) {
    return null;
  }

  const fullName = info.full_name?.trim();

  if (fullName) {
    return fullName;
  }

  const composed = [info.first_name, info.last_name]
    .map((namePart) => namePart?.trim())
    .filter(Boolean)
    .join(' ');

  return composed || null;
};
